import type { DecalStyle, SpoilerStyle, VisualTuning, WheelStyle } from '../../shared/types.js';
import { escapeHtml, safeColour } from './html.js';
import { t } from '../i18n/index.js';

/**
 * The garage's styling panel: one-click liveries, curated swatches and
 * picture chips instead of bare selects, so a player can make a car their own
 * in a couple of taps and always find the way back to the factory look.
 * Everything here is cosmetic; performance lives in the crew module.
 */

type ColourSlot = 'paint' | 'accent' | 'rims' | 'neon';

export interface LiveryPreset {
  id: string;
  name: string;
  paint: string;
  accent: string;
  rims: string;
  neon: string;
  neonEnabled: boolean;
  decal: DecalStyle;
}

export const LIVERY_PRESETS: readonly LiveryPreset[] = [
  { id: 'midnight', name: 'Midnight', paint: '#141821', accent: '#57e9ff', rims: '#1b1d19', neon: '#57e9ff', neonEnabled: true, decal: 'twin-stripe' },
  { id: 'inferno', name: 'Inferno', paint: '#ff3b2f', accent: '#ffb000', rims: '#20242b', neon: '#ff6a1f', neonEnabled: true, decal: 'center-stripe' },
  { id: 'toxic', name: 'Toxic', paint: '#20242b', accent: '#a9ff2f', rims: '#a9ff2f', neon: '#a9ff2f', neonEnabled: true, decal: 'twin-stripe' },
  { id: 'arctic', name: 'Arctic', paint: '#eef3f6', accent: '#2f6bff', rims: '#bcc3c9', neon: '#9fd8ff', neonEnabled: false, decal: 'center-stripe' },
  { id: 'synthwave', name: 'Synthwave', paint: '#6829ff', accent: '#ff3fa4', rims: '#e8ecef', neon: '#ff2fd0', neonEnabled: true, decal: 'none' },
  { id: 'heritage', name: 'Heritage', paint: '#1f4fff', accent: '#ffffff', rims: '#d6a62e', neon: '#45f5ff', neonEnabled: false, decal: 'twin-stripe' },
  { id: 'sunset', name: 'Sunset', paint: '#ff5a1f', accent: '#17191d', rims: '#41464e', neon: '#ffb000', neonEnabled: false, decal: 'none' },
];

const SWATCHES: Record<ColourSlot, readonly { colour: string; name: string }[]> = {
  paint: [
    { colour: '#f7f9fb', name: 'Pearl white' },
    { colour: '#20242b', name: 'Graphite' },
    { colour: '#ff2f45', name: 'Race red' },
    { colour: '#ff5a1f', name: 'Blaze orange' },
    { colour: '#ffd23f', name: 'Taxi yellow' },
    { colour: '#35c9b6', name: 'Lagoon teal' },
    { colour: '#2f6bff', name: 'Rally blue' },
    { colour: '#6829ff', name: 'Ultraviolet' },
    { colour: '#ff3fa4', name: 'Hot pink' },
    { colour: '#566449', name: 'Field olive' },
  ],
  accent: [
    { colour: '#101014', name: 'Gloss black' },
    { colour: '#ffffff', name: 'White' },
    { colour: '#ff3b30', name: 'Red' },
    { colour: '#ffb000', name: 'Amber' },
    { colour: '#a9ff2f', name: 'Acid lime' },
    { colour: '#57e9ff', name: 'Cyan' },
    { colour: '#ff2fd0', name: 'Magenta' },
    { colour: '#7a5cff', name: 'Violet' },
  ],
  rims: [
    { colour: '#1b1d19', name: 'Satin black' },
    { colour: '#41464e', name: 'Gunmetal' },
    { colour: '#bcc3c9', name: 'Silver' },
    { colour: '#e8ecef', name: 'White' },
    { colour: '#d6a62e', name: 'Gold' },
    { colour: '#ff3b30', name: 'Red' },
    { colour: '#2f6bff', name: 'Blue' },
    { colour: '#a9ff2f', name: 'Lime' },
  ],
  neon: [
    { colour: '#57e9ff', name: 'Ice cyan' },
    { colour: '#ff2fd0', name: 'Magenta' },
    { colour: '#a9ff2f', name: 'Acid' },
    { colour: '#ff3b30', name: 'Red' },
    { colour: '#ffb000', name: 'Amber' },
    { colour: '#7a5cff', name: 'Violet' },
    { colour: '#ffffff', name: 'White' },
    { colour: '#2f6bff', name: 'Blue' },
  ],
};

const SLOT_LABELS: Record<ColourSlot, string> = { paint: 'BODY', accent: 'ACCENT', rims: 'RIMS', neon: 'NEON' };

const SPOILERS: readonly { value: SpoilerStyle; label: string; icon: string }[] = [
  { value: 'none', label: 'NONE', icon: '<path d="M3 17h26"/><path d="M6 17c2-5 6-7 11-7s8 2 10 7"/>' },
  { value: 'ducktail', label: 'DUCKTAIL', icon: '<path d="M3 17h26"/><path d="M6 17c2-5 6-7 11-7s7 1 9 4l3-2"/>' },
  { value: 'gt', label: 'GT WING', icon: '<path d="M3 17h26"/><path d="M6 17c2-5 6-7 11-7s7 2 9 5"/><path d="M24 12V6m-3 0h9"/>' },
];

const WHEELS: readonly { value: WheelStyle; label: string; icon: string }[] = [
  { value: 'five-spoke', label: '5-SPOKE', icon: '<circle cx="16" cy="12" r="9"/><path d="M16 12V4M16 12l7.6 2.5M16 12l-7.6 2.5M16 12l4.7 6.5M16 12l-4.7 6.5"/>' },
  { value: 'mesh', label: 'MESH', icon: '<circle cx="16" cy="12" r="9"/><path d="M10 6l12 12M22 6L10 18M7 12h18M16 3v18"/>' },
  { value: 'turbine', label: 'TURBINE', icon: '<circle cx="16" cy="12" r="9"/><path d="M16 12c0-4 2-6 5-7M16 12c4 0 6 2 7 5M16 12c0 4-2 6-5 7M16 12c-4 0-6-2-7-5"/>' },
];

const DECALS: readonly { value: DecalStyle; label: string; icon: string; reward?: 'chevron' | 'circuit'; level?: number }[] = [
  { value: 'none', label: 'CLEAN', icon: '<rect x="8" y="3" width="16" height="18" rx="5"/>' },
  { value: 'center-stripe', label: 'CENTER', icon: '<rect x="8" y="3" width="16" height="18" rx="5"/><path d="M16 3v18" stroke-width="3"/>' },
  { value: 'twin-stripe', label: 'TWIN', icon: '<rect x="8" y="3" width="16" height="18" rx="5"/><path d="M13.5 3v18M18.5 3v18" stroke-width="2"/>' },
  { value: 'chevron', label: 'CHEVRON', reward: 'chevron', level: 2, icon: '<rect x="8" y="3" width="16" height="18" rx="5"/><path d="M10 13l6-5 6 5M10 18l6-5 6 5"/>' },
  { value: 'circuit', label: 'CIRCUIT', reward: 'circuit', level: 4, icon: '<rect x="8" y="3" width="16" height="18" rx="5"/><path d="M12 3v6h4v6h-3v6M20 3v4h-2M20 21v-5h2"/>' },
];

export interface TuningPanelOptions {
  tuning: VisualTuning;
  factory: VisualTuning;
  bike: boolean;
  /** Whether a career-locked decal is available. */
  decalUnlocked: (reward: 'chevron' | 'circuit') => boolean;
}

export function tuningPanelHtml({ tuning, factory, bike, decalUnlocked }: TuningPanelOptions): string {
  const preset = activePresetId(tuning, factory);
  const stanceCm = Math.round(tuning.stance * 100);
  const presetButton = (id: string, name: string, colours: Pick<VisualTuning, 'paint' | 'accent' | 'rims'>) => (
    `<button type="button" class="livery-chip" data-livery="${id}" aria-pressed="${preset === id}" style="--p:${safeColour(colours.paint)};--a:${safeColour(colours.accent)};--r:${safeColour(colours.rims)}"><i aria-hidden="true"></i><span>${escapeHtml(name)}</span></button>`
  );
  return `
    <section class="tune-block tune-liveries" aria-labelledby="tune-livery-label">
      <header><h4 id="tune-livery-label">${t('LIVERY')}</h4><small>${t('One tap styles the whole car · fine-tune below')}</small></header>
      <div class="livery-row">
        ${presetButton('factory', t('Factory'), factory)}
        ${LIVERY_PRESETS.map((entry) => presetButton(entry.id, entry.name, entry)).join('')}
        <button type="button" class="livery-chip livery-shuffle" data-livery="shuffle" title="${t('Roll a random, colour-matched build')}"><i aria-hidden="true">⚄</i><span>${t('Shuffle')}</span></button>
      </div>
    </section>

    <section class="tune-block tune-colours" aria-labelledby="tune-colour-label">
      <header><h4 id="tune-colour-label">${t('COLOURS')}</h4><small>${t('Tap a swatch, or ＋ for any colour')}</small></header>
      ${(Object.keys(SLOT_LABELS) as ColourSlot[]).map((slot) => colourRow(slot, tuning[slot], slot === 'neon' ? tuning.neonEnabled : undefined)).join('')}
    </section>

    <section class="tune-block tune-parts" aria-labelledby="tune-parts-label">
      <header><h4 id="tune-parts-label">${t('BODY KIT')}</h4><small>${t('Looks only · performance comes from the crew module')}</small></header>
      ${bike
        ? `<div class="chip-row" role="group" aria-label="${t('Spoiler')}"><span class="chip-label">${t('AERO')}</span><p class="chip-note">${t('Bikes run a fixed fairing.')}</p></div>`
        : chipRow('spoiler', 'SPOILER', SPOILERS.map((entry) => ({ ...entry, locked: false })), tuning.spoiler)}
      ${chipRow('wheels', 'WHEELS', WHEELS.map((entry) => ({ ...entry, locked: false })), tuning.wheels)}
      ${chipRow('decal', 'GRAPHICS', DECALS.map((entry) => ({
        ...entry,
        locked: entry.reward ? !decalUnlocked(entry.reward) : false,
      })), tuning.decal)}
      <div class="stance-row">
        <label class="chip-label" for="tuning-stance">${t('STANCE')}</label>
        <div class="stance-track">
          <input id="tuning-stance" name="tuning-stance" type="range" min="-0.08" max="0.08" step="0.01" value="${tuning.stance}" data-tune="stance" list="stance-ticks" aria-valuetext="${stanceText(stanceCm)}">
          <datalist id="stance-ticks"><option value="-0.08"></option><option value="0"></option><option value="0.08"></option></datalist>
          <div class="stance-scale" aria-hidden="true"><span>${t('SLAMMED')}</span><span>${t('STOCK')}</span><span>${t('LIFTED')}</span></div>
        </div>
        <output for="tuning-stance" data-stance-output>${stanceText(stanceCm)}</output>
      </div>
    </section>

    <div class="tune-footer">
      <button type="button" class="tune-reset" data-livery="factory" ${preset === 'factory' ? 'disabled' : ''}>↺ ${t('RESET TO FACTORY')}</button>
    </div>`;
}

export function stanceText(centimetres: number): string {
  if (centimetres === 0) return t('STOCK');
  return `${centimetres > 0 ? '+' : '−'}${Math.abs(centimetres)} CM`;
}

function colourRow(slot: ColourSlot, value: string, neonEnabled?: boolean): string {
  const current = safeColour(value).toLowerCase();
  const known = SWATCHES[slot].some((swatch) => swatch.colour === current);
  const off = neonEnabled === false;
  return `
    <div class="colour-row ${off ? 'is-off' : ''}" role="group" aria-label="${t('{slot} colour', { slot: t(SLOT_LABELS[slot]) })}">
      <span class="chip-label">${t(SLOT_LABELS[slot])}</span>
      <div class="swatch-row">
        ${SWATCHES[slot].map((swatch) => `<button type="button" class="swatch" data-swatch="${slot}" data-colour="${swatch.colour}" aria-pressed="${swatch.colour === current}" title="${escapeHtml(t(swatch.name))}" aria-label="${escapeHtml(t(swatch.name))}" style="--swatch:${swatch.colour}"></button>`).join('')}
        <label class="swatch swatch-custom" title="${t('Custom colour')}" aria-pressed="${!known}" style="--swatch:${current}"><input name="tuning-${slot}" type="color" value="${current}" data-tune="${slot}" aria-label="${t('Custom {slot} colour', { slot: t(SLOT_LABELS[slot]).toLowerCase() })}"><span aria-hidden="true">＋</span></label>
      </div>
      ${neonEnabled === undefined ? '' : `<label class="neon-switch"><input name="tuning-underglow" type="checkbox" role="switch" data-tune="neonEnabled" ${neonEnabled ? 'checked' : ''}><span><i></i>${neonEnabled ? t('UNDERGLOW ON') : t('UNDERGLOW OFF')}</span></label>`}
    </div>`;
}

function chipRow<T extends string>(
  key: 'spoiler' | 'wheels' | 'decal',
  label: string,
  entries: readonly { value: T; label: string; icon: string; locked: boolean; level?: number }[],
  value: T,
): string {
  return `
    <div class="chip-row" role="radiogroup" aria-label="${t(label)}">
      <span class="chip-label">${t(label)}</span>
      <div class="part-chips">
        ${entries.map((entry) => `<label class="part-chip ${entry.locked ? 'locked' : ''}" title="${entry.locked ? t('Unlocks at career level {level}', { level: entry.level ?? '' }) : t(entry.label)}"><input type="radio" name="tuning-${key}" value="${entry.value}" data-tune="${key}" ${entry.value === value ? 'checked' : ''} ${entry.locked ? 'disabled' : ''}><span><svg viewBox="0 0 32 24" aria-hidden="true">${entry.icon}</svg><b>${t(entry.label)}</b>${entry.locked ? `<small>${t('LV{level}', { level: entry.level ?? '' })}</small>` : ''}</span></label>`).join('')}
      </div>
    </div>`;
}

function sameColour(left: string, right: string): boolean {
  return left.toLowerCase() === right.toLowerCase();
}

/** Which livery chip the current build matches, if any. */
export function activePresetId(tuning: VisualTuning, factory: VisualTuning): string | null {
  const matches = (preset: Omit<LiveryPreset, 'id' | 'name'>) => sameColour(tuning.paint, preset.paint)
    && sameColour(tuning.accent, preset.accent)
    && sameColour(tuning.rims, preset.rims)
    && sameColour(tuning.neon, preset.neon)
    && tuning.neonEnabled === preset.neonEnabled
    && tuning.decal === preset.decal;
  if (matches(factory) && tuning.spoiler === factory.spoiler && tuning.wheels === factory.wheels && tuning.stance === factory.stance) return 'factory';
  return LIVERY_PRESETS.find((preset) => matches(preset))?.id ?? null;
}

/** Applies a livery preset's colours and graphics, keeping the chosen parts. */
export function applyLivery(tuning: VisualTuning, preset: LiveryPreset, decalAllowed: (decal: DecalStyle) => boolean): void {
  tuning.paint = preset.paint;
  tuning.accent = preset.accent;
  tuning.rims = preset.rims;
  tuning.neon = preset.neon;
  tuning.neonEnabled = preset.neonEnabled;
  tuning.decal = decalAllowed(preset.decal) ? preset.decal : 'none';
}

/**
 * A random build whose colours belong together: the accent sits opposite the
 * body hue, rims are a neutral or the accent, and the underglow echoes it.
 */
export function shuffledTuning(
  current: VisualTuning,
  bike: boolean,
  decalAllowed: (decal: DecalStyle) => boolean,
  random: () => number = Math.random,
): VisualTuning {
  const pick = <T>(values: readonly T[]): T => values[Math.min(values.length - 1, Math.floor(random() * values.length))]!;
  const hue = random() * 360;
  const dark = random() < 0.3;
  const paint = hslToHex(hue, dark ? 0.18 : 0.78, dark ? 0.14 : 0.52);
  const accentHue = (hue + 150 + random() * 60) % 360;
  const accent = hslToHex(accentHue, 0.95, 0.56);
  const rims = pick(['#1b1d19', '#41464e', '#bcc3c9', '#d6a62e', accent]);
  return {
    ...current,
    paint,
    accent,
    rims,
    neon: hslToHex(accentHue, 1, 0.6),
    neonEnabled: random() < 0.6,
    spoiler: bike ? current.spoiler : pick(SPOILERS).value,
    wheels: pick(WHEELS).value,
    decal: pick(DECALS.filter((entry) => decalAllowed(entry.value))).value,
    stance: Math.round((random() * 0.1 - 0.06) * 100) / 100,
  };
}

function hslToHex(hue: number, saturation: number, lightness: number): string {
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const segment = hue / 60;
  const x = chroma * (1 - Math.abs((segment % 2) - 1));
  const [r, g, b] = segment < 1 ? [chroma, x, 0]
    : segment < 2 ? [x, chroma, 0]
      : segment < 3 ? [0, chroma, x]
        : segment < 4 ? [0, x, chroma]
          : segment < 5 ? [x, 0, chroma]
            : [chroma, 0, x];
  const m = lightness - chroma / 2;
  return `#${[r, g, b].map((channel) => Math.round((channel + m) * 255).toString(16).padStart(2, '0')).join('')}`;
}
